import { useCallback, useEffect, useRef, useState } from 'react';

import { useExperience } from '@cctv/contexts/ExperienceContext';
import { useExperienceState } from '@cctv/contexts/ExperienceStateContext';
import Avatar from '@cctv/experiences/GuessWho/Avatar';
import { useSubmitBuzzerResponse } from '@cctv/hooks/useSubmitBuzzerResponse';
import { BuzzerBlock } from '@cctv/types';

import styles from './Buzzer.module.scss';

const AVATAR_DISPLAY_SIZE = 280;

function playBuzzerSound() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(140, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(60, ctx.currentTime + 0.5);

    gain.gain.setValueAtTime(0.35, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  } catch {
    // AudioContext not available — silent fail
  }
}

interface Particle {
  id: number;
  x: number;
  y: number;
  angle: number;
  speed: number;
  size: number;
  opacity: number;
  color: string;
}

const PARTICLE_COLORS = ['#ff4911', '#ff6b3d', '#ffaa00', '#ffe066', '#ffffff'];

function createParticles(cx: number, cy: number): Particle[] {
  return Array.from({ length: 16 }, (_, i) => ({
    id: i,
    x: cx,
    y: cy,
    angle: (Math.PI * 2 * i) / 16 + (Math.random() - 0.5) * 0.4,
    speed: 3 + Math.random() * 5,
    size: 4 + Math.random() * 6,
    opacity: 1,
    color: PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)],
  }));
}

interface BuzzerProps {
  block: BuzzerBlock;
  viewContext?: 'participant' | 'monitor' | 'manage';
}

export default function Buzzer({ block, viewContext = 'participant' }: BuzzerProps) {
  const { experience, monitorView } = useExperience();
  const { submissionState } = useExperienceState();
  const { submitBuzzerResponse, isLoading } = useSubmitBuzzerResponse();
  const priorSubmission = submissionState[block.id];
  const [buzzed, setBuzzed] = useState(false);
  const [showBuzzed, setShowBuzzed] = useState(false);
  const hasBuzzed = !!priorSubmission || buzzed;
  const shouldShowBuzzed = !!priorSubmission || showBuzzed;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animRef = useRef<number>(0);

  const allResponses = block.responses?.all_responses ?? [];
  const firstResponse = allResponses[0] ?? null;

  const handleBuzz = async () => {
    if (hasBuzzed || isLoading) return;
    playBuzzerSound();
    setBuzzed(true);
    const result = await submitBuzzerResponse(block.id);
    if (!result?.success) {
      setBuzzed(false);
    } else {
      setTimeout(() => setShowBuzzed(true), 800);
    }
  };

  const spawnExplosion = useCallback((cx: number, cy: number) => {
    if (particlesRef.current.length > 0) return;
    particlesRef.current = createParticles(cx, cy);

    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const tick = () => {
      const currentCanvas = canvasRef.current;
      if (!currentCanvas) return;

      particlesRef.current = particlesRef.current
        .map((p) => ({
          ...p,
          x: p.x + Math.cos(p.angle) * p.speed,
          y: p.y + Math.sin(p.angle) * p.speed,
          speed: p.speed * 0.92,
          opacity: p.opacity - 0.04,
          size: p.size * 0.96,
        }))
        .filter((p) => p.opacity > 0);

      ctx.clearRect(0, 0, currentCanvas.width, currentCanvas.height);
      for (const p of particlesRef.current) {
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      if (particlesRef.current.length > 0) {
        animRef.current = requestAnimationFrame(tick);
      }
    };

    animRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  if (viewContext === 'monitor' || viewContext === 'manage') {
    if (!firstResponse) {
      return (
        <div className={styles.monitorWaiting}>
          <p className={styles.monitorLabel}>
            {block.payload.prompt || 'Contestants be ready to buzz in!'}
          </p>
        </div>
      );
    }

    const sourceView = monitorView ?? experience;
    const allParticipants = [...(sourceView?.participants ?? []), ...(sourceView?.hosts ?? [])];
    const winner = allParticipants.find((p) => p.id === firstResponse.experience_participant_id);
    const avatar = firstResponse.avatar;
    const hasAvatar = !!(avatar?.image || avatar?.strokes?.length);

    return (
      <div className={styles.monitorWinner}>
        <p className={styles.monitorLabel}>
          {block.payload.prompt || 'Contestants be ready to buzz in!'}
        </p>
        {hasAvatar ? (
          <div className={styles.avatarWrap}>
            <Avatar avatar={avatar} size={AVATAR_DISPLAY_SIZE} />
          </div>
        ) : (
          <div className={styles.avatarPlaceholder} />
        )}
        <p className={styles.winnerName}>{winner?.name ?? 'Unknown'}</p>
      </div>
    );
  }

  if (shouldShowBuzzed) {
    return (
      <div className={styles.buzzedState}>
        <p className={styles.buzzedText}>Buzzed!</p>
      </div>
    );
  }

  return (
    <div className={styles.participantRoot}>
      <p className={styles.label}>{block.payload.label || 'Buzz In'}</p>
      <div className={styles.buzzerArea}>
        <button
          className={styles.buzzerButton}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            spawnExplosion(rect.left + rect.width / 2, rect.top + rect.height / 2);
            handleBuzz();
          }}
          disabled={hasBuzzed || isLoading}
          aria-label="Buzz in"
        />
        <canvas ref={canvasRef} className={styles.particleLayer} />
      </div>
    </div>
  );
}
